import { Head, useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { AuthLayout } from '@/components/layouts/AuthLayout'

interface UserData {
  email: string
  password: string
  password_confirmation: string
  pseudo: string
  first_name: string
  last_name: string
  rpps: string
  siret: string
  specialty: string
}

interface RegisterForm {
  user: UserData
}

interface CommunityProps {
  name: string
  slug: string
  has_professional_id: boolean
  has_specialty: boolean
  professional_id_label?: string
  professional_id_hint?: string
  professional_id_field?: string
  professional_id_length?: number
  specialties: string[]
  primary_color: string
}

interface Props {
  community: CommunityProps
}

const DEFAULT_SPECIALTIES = [
  'Généraliste',
  'Dermatologue',
  'Pédiatre',
  'Urgentiste',
  'Ophtalmologue',
  'Orthoptiste',
  'Gériatre',
  'TeamTessanMed',
]

export default function Register({ community }: Props) {
  const specialties = community?.specialties?.length > 0 ? community.specialties : DEFAULT_SPECIALTIES
  const communitySlug = community?.slug || 'medecins'
  const communityName = community?.name || 'Médecins'
  const isPharmacien = communitySlug === 'pharmaciens'
  const hasProfessionalId = community?.has_professional_id !== false
  const hasSpecialty = community?.has_specialty !== false && !isPharmacien
  const professionalIdLabel = community?.professional_id_label || 'Numéro RPPS'
  const professionalIdHint = community?.professional_id_hint || '11 chiffres - visible sur votre carte CPS'
  const professionalIdLength = community?.professional_id_length || 11

  const postUrl = communitySlug === 'medecins' ? '/users' : `/${communitySlug}/inscription`

  const { data, setData, post, processing, errors } = useForm<RegisterForm>({
    user: {
      email: '',
      password: '',
      password_confirmation: '',
      pseudo: '',
      first_name: '',
      last_name: '',
      rpps: '',
      siret: '',
      specialty: '',
    },
  })

  const setUserData = (field: keyof UserData, value: string) => {
    setData('user', { ...data.user, [field]: value })
  }

  // Gestion de l'identifiant professionnel (RPPS ou SIRET)
  const professionalIdValue = isPharmacien ? data.user.siret : data.user.rpps
  const setProfessionalIdValue = (value: string) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, professionalIdLength)
    if (isPharmacien) {
      setUserData('siret', cleanedValue)
    } else {
      setUserData('rpps', cleanedValue)
    }
  }

  // Errors from Devise come as user.field format
  const userErrors = errors as Record<string, string>
  const professionalIdError = isPharmacien ? userErrors['user.siret'] : userErrors['user.rpps']

  const submit = (e: FormEvent) => {
    e.preventDefault()
    post(postUrl)
  }

  return (
    <AuthLayout>
      <Head title={`Inscription ${communityName} - Tessan Community`} />

      <div className="text-center mb-4">
        <h1 className="text-2xl font-heading font-bold text-text-primary">Inscription</h1>
        <p className="text-text-secondary text-sm mt-1">Rejoignez la communauté {communityName}</p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {/* Prénom et Nom */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Prénom"
            type="text"
            value={data.user.first_name}
            onChange={(e) => setUserData('first_name', e.target.value)}
            error={userErrors['user.first_name']}
            placeholder="Jean"
            required
          />
          <Input
            label="Nom"
            type="text"
            value={data.user.last_name}
            onChange={(e) => setUserData('last_name', e.target.value)}
            error={userErrors['user.last_name']}
            placeholder="Dupont"
            required
          />
        </div>

        {/* Pseudo et Spécialité */}
        <div className={hasSpecialty ? 'grid grid-cols-2 gap-3' : ''}>
          <Input
            label="Pseudo"
            type="text"
            value={data.user.pseudo}
            onChange={(e) => setUserData('pseudo', e.target.value.slice(0, 30))}
            error={userErrors['user.pseudo']}
            placeholder={isPharmacien ? 'Pharmacie Dupont' : 'Dr Dupont'}
            hint="2-30 caractères"
            required
          />
          {hasSpecialty && (
            <Select
              label="Spécialité"
              value={data.user.specialty}
              onChange={(e) => setUserData('specialty', e.target.value)}
              error={userErrors['user.specialty']}
              options={specialties}
              placeholder="Sélectionnez"
              required
            />
          )}
        </div>

        {/* Identifiant professionnel + Email */}
        {hasProfessionalId ? (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={professionalIdLabel}
              type="text"
              value={professionalIdValue}
              onChange={(e) => setProfessionalIdValue(e.target.value)}
              error={professionalIdError}
              placeholder={isPharmacien ? '12345678901234' : '12345678901'}
              maxLength={professionalIdLength}
              required
              hint={professionalIdHint}
            />
            <Input
              label="Email"
              type="email"
              value={data.user.email}
              onChange={(e) => setUserData('email', e.target.value)}
              error={userErrors['user.email']}
              placeholder="jean.dupont@email.com"
              required
            />
          </div>
        ) : (
          <Input
            label="Email"
            type="email"
            value={data.user.email}
            onChange={(e) => setUserData('email', e.target.value)}
            error={userErrors['user.email']}
            placeholder="jean.dupont@email.com"
            required
          />
        )}

        {/* Mot de passe et Confirmation */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Mot de passe"
            type="password"
            value={data.user.password}
            onChange={(e) => setUserData('password', e.target.value)}
            error={userErrors['user.password']}
            required
            hint="Minimum 8 caractères"
          />
          <Input
            label="Confirmer"
            type="password"
            value={data.user.password_confirmation}
            onChange={(e) => setUserData('password_confirmation', e.target.value)}
            error={userErrors['user.password_confirmation']}
            required
          />
        </div>

        <Button type="submit" className="w-full mt-4" disabled={processing}>
          {processing ? 'Inscription...' : "S'inscrire"}
        </Button>
      </form>

      <p className="text-center text-text-secondary text-sm mt-4">
        Déjà un compte ?{' '}
        <Link href="/users/sign_in" className="text-tessan-green hover:underline font-medium">
          Se connecter
        </Link>
      </p>
    </AuthLayout>
  )
}
